/**
 * shared/common.ts —— 通用组件：空态 / 轻提示条 / 弹窗 / 变动行高亮
 * 来历：一期 [SEC:common] 段平移，行为一字未改，只加类型。
 *
 * ⚠ 弹窗的三条交互约定是评审定过的，不要"顺手优化"：
 *   ① 点遮罩【不关闭】——防误触丢失填写内容；
 *   ② Esc 关闭前若有改动走二次确认（dirty 回调）；
 *   ③ 焦点进入弹窗，关闭后还给 opener。
 *
 * ⚠ 2026-08-27 加了开合动画（入场在 CSS 里，关闭由 `dropModal` 加 `.closing` 再延迟摘除）。
 *   动画只改透明度与位移，reduced-motion 下 `15_media.css` 会整体关掉；
 *   ⚠ **关闭仍是同步生效的**：出栈、onClose、焦点归还都在同一帧完成，只有摘节点晚 140ms。
 */
import { $, esc } from './dom.js';
import { IC } from './icons.js';
import { enhanceSelects } from './select.js';
import { enhanceDates } from './datepick.js';

/** 空态。size 可传 'sm'（详情页区块内）或留空（整页） */
export function emptyHtml(icon: string, title: string, desc?: string, action?: string, size?: string): string {
  return `<div class="empty ${size || ''}">
    <span class="ei">${icon}</span>
    <div class="et">${esc(title)}</div>
    ${desc ? `<div class="ed">${esc(desc)}</div>` : ''}
    ${action || ''}
  </div>`;
}

/** 保存成功后的 3 秒轻提示条 */
export function toast(msg: string): void {
  document.querySelectorAll('.toast').forEach((n) => n.remove());
  const d = document.createElement('div');
  d.className = 'toast';
  d.setAttribute('role', 'status');
  d.innerHTML = IC.check + '<span>' + esc(msg) + '</span>';
  document.body.appendChild(d);
  setTimeout(() => d.remove(), 3000);
}

/* ---------- 变动行高亮 1.5 秒：渲染发生在路由之后，故用待办标记，在 render 末尾消费 ---------- */
let pendingFlash: string | null = null;
export function flashAfterRender(id: string): void {
  pendingFlash = id;
}
export function doFlash(): void {
  if (!pendingFlash) return;
  const tr = $('#main')?.querySelector<HTMLElement>(`tr[data-team="${pendingFlash}"]`);
  pendingFlash = null;
  if (!tr) return;
  tr.classList.remove('flash');
  void tr.offsetWidth;
  tr.classList.add('flash');
  tr.focus({ preventScroll: true });
  tr.scrollIntoView({ block: 'nearest' });
}

/* ---------- 弹窗 ---------- */

export interface ModalOptions {
  title: string;
  body: string;
  footer?: string;
  /**
   * 标题右侧的小字（2026-08-22 新增）。纯文本，**会被 esc()**，不要传 HTML。
   * 用途：把"谁在什么时候建的"这类元信息从正文里挪到标题栏，正文只留真正的内容。
   * ⚠ 别拿它塞长句子——`.modal-h` 是定高 56px 的一行，超了会把标题挤没。
   */
  titleMeta?: string;
  /** 窄 sm 480 / 中（默认）640 / **中宽 md 760**（2026-08-27 加，`27_checkin_modal.css`）/ 宽 lg 880 */
  size?: '' | 'sm' | 'md' | 'lg';
  /** 返回 true 表示有未保存改动，关闭前走二次确认 */
  dirty?: () => boolean;
  onMount?: (root: HTMLElement, rec: ModalRec) => void;
  onClose?: () => void;
}

export interface ModalRec {
  mask: HTMLElement;
  opt: ModalOptions;
  opener: Element | null;
}

const MODALS: ModalRec[] = [];

export function openModal(o: ModalOptions): ModalRec {
  const mask = document.createElement('div');
  mask.className = 'mask';
  mask.innerHTML = `<div class="modal ${o.size || ''}" role="dialog" aria-modal="true" aria-label="${esc(o.title)}">
      <div class="modal-h"><h3>${esc(o.title)}</h3>${
        o.titleMeta ? `<span class="modal-hm">${esc(o.titleMeta)}</span>` : ''
      }<button class="modal-x" data-mclose aria-label="关闭">${IC.x}</button></div>
      <div class="modal-b">${o.body}</div>
      <div class="modal-f">${o.footer || ''}</div>
    </div>`;
  document.body.appendChild(mask);
  /* ⚠ 必须在找首个可聚焦元素【之前】同步接管下拉：Observer 是微任务，晚一步，
     那样焦点会落在紧接着就被隐藏的原生 select 上 */
  enhanceSelects(mask);
  enhanceDates(mask);
  const rec: ModalRec = { mask, opt: o, opener: document.activeElement };
  MODALS.push(rec);
  if (o.onMount) o.onMount(mask, rec);
  const first =
    /* ⚠ `[data-cklb]` 是打卡详情里的照片缩略图（2026-08-27 起是按钮）——
       它排在正文最前面，不排除掉的话一开窗焦点就落在第一张照片上，
       而这个弹窗是**只读**的，焦点该落在页脚的主按钮上。 */
    mask.querySelector<HTMLElement>(
      '.modal-b input:not([readonly]):not([disabled]):not(.dp-native), .modal-b .ps-btn:not([disabled]), .modal-b button:not([disabled]):not([data-cklb])',
    ) ||
    mask.querySelector<HTMLElement>('.modal-f .btn.primary:not([disabled])') ||
    mask.querySelector<HTMLElement>('[data-mclose]');
  if (first) first.focus();
  return rec;
}

export function closeModal(force?: boolean): void {
  const rec = MODALS[MODALS.length - 1];
  if (!rec) return;
  if (!force && rec.opt.dirty && rec.opt.dirty()) {
    confirmModal({
      title: '放弃未保存的修改',
      okText: '放弃修改',
      danger: true,
      html: '弹窗内有尚未保存的修改，关闭后这些内容将丢失。',
      onOk: () => dropModal(rec),
    });
    return;
  }
  dropModal(rec);
}

/**
 * 关闭动画时长，与 `27_checkin_modal.css` 的 `.mask.closing` 一致（改一处必须改两处）。
 * ⚠ 这段时间里 mask 还在 DOM 上，但 CSS 已把它设成 `pointer-events:none`，
 *   所以它既不挡点击，也不会被 `t.closest('.mask')` 命中（事件根本不落在它身上）。
 *   MODALS 已经先出栈，`hasModal()` / Esc 的判断不受影响。
 */
const CLOSE_MS = 140;

function dropModal(rec: ModalRec): void {
  const i = MODALS.indexOf(rec);
  if (i < 0) return;
  MODALS.splice(i, 1);
  /* 先出栈再淡出：出栈是同步的（逻辑上"已经关了"），淡出只是视觉收尾 */
  rec.mask.classList.add('closing');
  const el = rec.mask;
  setTimeout(() => el.remove(), CLOSE_MS);
  if (rec.opt.onClose) rec.opt.onClose();
  if (rec.opener instanceof HTMLElement) {
    try {
      rec.opener.focus();
    } catch {
      /* opener 可能已随上一次渲染被移除，忽略 */
    }
  }
}

/** 顶层弹窗是否存在——events 层判断 Esc 该关弹窗还是该走页面逻辑 */
export const hasModal = (): boolean => MODALS.length > 0;

/** 破坏性操作的二次确认：窄档，正文写清后果，主按钮警示色 */
export function confirmModal(o: {
  title: string;
  html: string;
  okText?: string;
  cancelText?: string;
  danger?: boolean;
  onOk: () => void;
}): ModalRec {
  return openModal({
    title: o.title,
    size: 'sm',
    body: `<p class="mtext">${o.html}</p>`,
    footer: `<button class="btn" data-mclose>${esc(o.cancelText || '取消')}</button>
            <button class="btn ${o.danger ? 'danger' : 'primary'}" data-ok>${esc(o.okText || '确认')}</button>`,
    onMount(root) {
      root.querySelector('[data-ok]')?.addEventListener('click', () => {
        closeModal(true);
        o.onOk();
      });
    },
  });
}
